import React, { useMemo } from "react";

type BaseRow = Record<string, string>;

interface TableProps<T extends BaseRow> {
  title?: string;
  headers: string[];
  hideAddRow?: boolean;
  nerveOptions?: string[];
  sideOptions?: string[];
  stimOptions?: string[];
  recOptions?: string[];
  muscleOptions?: string[];
  dropdownOptions?: Record<string, string[]>;
  rows: T[];
  setRows: React.Dispatch<React.SetStateAction<T[]>>;
  onClear?: () => void;
  isMotor?: boolean;
}

const omitSelectFor = new Set(["Nerve", "Stim", "Rec", "Muscle"]);

const getOptions = (
  header: string,
  props: Pick<
    TableProps<any>,
    | "nerveOptions"
    | "sideOptions"
    | "stimOptions"
    | "recOptions"
    | "muscleOptions"
    | "dropdownOptions"
  >
): string[] | undefined => {
  if (omitSelectFor.has(header)) return undefined;

  switch (header) {
    case "Nerve":
      return props.nerveOptions;
    case "Side":
      return props.sideOptions;
    case "Stim":
      return props.stimOptions;
    case "Rec":
      return props.recOptions;
    case "Muscle":
      return props.muscleOptions;
    default:
      return props.dropdownOptions?.[header];
  }
};

function Table<T extends BaseRow>(props: TableProps<T>) {
  const { title, headers, rows, setRows, onClear, hideAddRow, ...opt } = props;
  const hasTitle = Boolean(title && title.trim().length > 0);

  const makeEmptyRow = (): T =>
    headers.reduce((acc, header) => ({ ...acc, [header]: "" }), {} as T);

  const updateCell = (rowIndex: number, key: string, value: string) =>
    setRows((prev) =>
      prev.map((row, index) => (index === rowIndex ? { ...row, [key]: value } : row))
    );

  const deleteRow = (rowIndex: number) =>
    setRows((prev) => prev.filter((_, index) => index !== rowIndex));

  const columnWidths = useMemo(() => {
    return headers.reduce((acc, header) => {
      const values = rows.map((row) => String(row[header] || ""));
      const options = getOptions(header, opt) || [];
      const longest = Math.max(
        header.length,
        ...values.map((value) => value.length),
        ...options.map((option) => option.length),
        4
      );

      let minWidth = 8;
      let maxWidth = 28;

      if (header === "Side") {
        minWidth = 5;
        maxWidth = 6;
      } else if (
        ["Lat(ms)", "Amp(mV)", "Amp(uV)", "Dist(cm)", "CV(m/s)", "Dur(ms)"].includes(header)
      ) {
        minWidth = 7;
        maxWidth = 10;
      } else if (["IA", "Fibs", "PSW", "Disch", "Amp", "Dur", "Phase", "IntPat"].includes(header)) {
        minWidth = 8;
        maxWidth = 14;
      } else if (header === "Note") {
        minWidth = 12;
        maxWidth = 22;
      }

      acc[header] = `${Math.max(minWidth, Math.min(longest + 2, maxWidth))}ch`;
      return acc;
    }, {} as Record<string, string>);
  }, [
    headers,
    rows,
    opt.dropdownOptions,
    opt.muscleOptions,
    opt.nerveOptions,
    opt.recOptions,
    opt.sideOptions,
    opt.stimOptions,
  ]);

  return (
    <div className="surface-card table-card">
      {hasTitle && (
        <div className="card-header">
          <h3 className="card-title">{title!.trim()}</h3>
        </div>
      )}

      <div className="table-shell">
        {rows.length > 0 ? (
          <table className="app-table">
            <thead>
              <tr>
                {headers.map((header) => (
                  <th key={header}>{header}</th>
                ))}
                <th></th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {headers.map((header) => {
                    const options = getOptions(header, opt);
                    const inputStyle: React.CSSProperties = {
                      width: columnWidths[header],
                      minWidth: columnWidths[header],
                    };

                    return (
                      <td key={header}>
                        {options ? (
                          <select
                            className="table-select"
                            style={inputStyle}
                            value={row[header] || ""}
                            onChange={(event) =>
                              updateCell(rowIndex, header, event.target.value)
                            }
                          >
                            <option value=""></option>
                            {options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            className="table-input"
                            style={inputStyle}
                            value={row[header] || ""}
                            onChange={(event) =>
                              updateCell(rowIndex, header, event.target.value)
                            }
                          />
                        )}
                      </td>
                    );
                  })}

                  <td className="table-delete-cell">
                    <button
                      className="table-delete-btn"
                      onClick={() => deleteRow(rowIndex)}
                      title="Delete row"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="table-placeholder">
            No rows yet. Add a preset or create rows manually to start editing.
          </div>
        )}
      </div>

      <div className="table-controls">
        {!hideAddRow && (
          <button
            onClick={() => setRows((prev) => [...prev, makeEmptyRow()])}
            className="app-button secondary small"
          >
            + Add Row
          </button>
        )}
        {onClear && (
          <button onClick={onClear} className="app-button subtle small">
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

export default Table;
